import * as constants from '../../common/constant';
import { setCursorPosition, decoupleReference, generateUniqueId, getBlockContentElement, getBlockModelById, removeFocusFromAllCells, projectEqualPercentFits, applyEqualPercent, changeColWidthToPxUnits, getWidthMode, getColgroupChildren } from '../../common/utils/index';
import { createElement } from '@syncfusion/ej2-base';
import { BlockFactory } from './block-factory';
import { findClosestParent } from '../../common/utils/dom';
/**
 * Manages core table block actions
 *
 * @hidden
 */
export class TableService {
    /**
     * Creates a new BlockCommandManager instance
     *
     * @param {BlockManager} manager The parent BlockManager instance
     */
    constructor(manager) {
        this.parent = manager;
    }
    /**
     * Creates and returns a fully rendered <tr> element for the given row model.
     *
     * @param {number} visualRowIndex - The visual row index in DOM (1-based, accounts for header if present)
     * @param {ITableBlockSettings} settings - Current table configuration (columns, enableHeader, enableRowNumbers, etc.)
     * @param {BlockModel} block - The parent table BlockModel
     * @param {TableRowModel} TableRowModel - The row data model to render
     * @returns {HTMLTableRowElement} The created <tr> element with all cells appended
     *
     * @hidden
     */
    createRow(visualRowIndex, settings, block, TableRowModel) {
        const rowEl = createElement('tr');
        const columns = settings.columns;
        rowEl.dataset.row = visualRowIndex.toString();
        // Row number cell
        if (settings.enableRowNumbers) {
            const rn = createElement('td', {
                className: 'e-row-number',
                attrs: { tabindex: '-1', 'aria-hidden': 'true', contenteditable: 'false' }
            });
            rn.textContent = (settings.enableHeader ? (visualRowIndex) : (visualRowIndex + 1)).toString();
            rowEl.appendChild(rn);
        }
        // Data cells
        columns.forEach((colModel, cIdx) => {
            const td = createElement('td');
            td.dataset.row = visualRowIndex.toString();
            td.dataset.col = cIdx.toString();
            td.tabIndex = 0;
            td.setAttribute('role', 'gridcell');
            const cell = (TableRowModel.cells).find((c) => c.columnId === colModel.id);
            const cellBlockContainer = createElement('div', {
                id: cell.id,
                className: 'e-cell-blocks-container'
            });
            if (cell) {
                cell.blocks.forEach((innerBlock) => {
                    const innerEl = this.parent.blockRenderer.createBlockElement(innerBlock);
                    cellBlockContainer.appendChild(innerEl);
                });
            }
            td.appendChild(cellBlockContainer);
            rowEl.appendChild(td);
        });
        return rowEl;
    }
    /**
     * Inserts a new row at the specified position in both model and DOM.
     * Automatically updates row numbers and dataset indices.
     *
     * @param {ITableRowInsertOptions} options - arguments needed for row insertion in specified position.
     * @returns {void}
     *
     * @hidden
     */
    addRowAt(options) {
        const { blockId, rowIndex, rowModel, isUndoRedoAction } = options;
        const oldBlock = decoupleReference(getBlockModelById(blockId, this.parent.getEditorBlocks()));
        const blockElement = this.parent.getBlockElementById(blockId);
        const table = blockElement.querySelector('table.e-table-element');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        this.removeCellFocus(table);
        const settings = block.properties;
        const tbody = table.tBodies[0];
        const newTableRowModel = rowModel || this.createRowModel(settings);
        settings.rows.splice(rowIndex, 0, newTableRowModel);
        // Render DOM row from model
        const newRowEl = this.createRow(rowIndex, settings, block, newTableRowModel);
        const rows = Array.from(tbody.rows);
        tbody.insertBefore(newRowEl, rows[rowIndex]);
        // Update dataset indices
        Array.from(tbody.rows).forEach((row, rIdx) => {
            row.dataset.row = settings.enableHeader ? (rIdx + 1).toString() : rIdx.toString();
            Array.from(row.cells).forEach((cell) => {
                if (cell.tagName === 'TD') {
                    cell.dataset.row = (rIdx + 1).toString();
                    cell.setAttribute('aria-rowindex', (rIdx + 1).toString());
                }
            });
        });
        this.addCellFocus(newRowEl.cells[0], true);
        this.updateRowNumbers(table, settings);
        const deCoupledRowModel = decoupleReference(newTableRowModel);
        this.parent.undoRedoAction.trackTableRowInsertionForUndoRedo({
            blockId, rowIndex, rowModel: deCoupledRowModel, isUndoRedoAction
        });
        this.triggerBlockUpdate(block, oldBlock);
    }
    /**
     * Inserts a new column at the specified position in both model and DOM.
     * Handles column model creation, width redistribution, and cell block containers.
     *
     * @param {ITableColumnInsertOptions} options - arguments needed for column insertion in specified position.
     * @returns {void}
     *
     * @hidden
     */
    addColumnAt(options) {
        const { blockId, colIndex, columnModel, columnCells, isUndoRedoAction } = options;
        const oldBlock = decoupleReference(getBlockModelById(blockId, this.parent.getEditorBlocks()));
        const blockElement = this.parent.getBlockElementById(blockId);
        const table = blockElement.querySelector('table.e-table-element');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const tableBlockElement = findClosestParent(table, '.' + constants.TABLE_BLOCK_CLS);
        const targetColIndex = colIndex;
        const settings = block.properties;
        const colIndicator = tableBlockElement.querySelector('.e-col-insert-handle');
        const colLine = tableBlockElement.querySelector('.e-col-hover-line');
        const colResizeHandle = tableBlockElement.querySelector('.e-col-resize-handle');
        const mode = getWidthMode(table);
        const containerWidth = table.clientWidth;
        this.updateDataColCount(table, false);
        const newCount = parseInt(table.getAttribute('data-col-counter'), 10);
        table.setAttribute('data-col-counter', newCount.toString());
        colIndicator.style.display = 'none';
        colLine.style.display = 'none';
        colResizeHandle.style.display = 'none';
        // Insert TableColumnModel into settings.columns
        const newColModel = columnModel || this.createColumnModel(newCount);
        const newColCells = [];
        settings.columns.splice(targetColIndex, 0, newColModel);
        // Insert a new cell into each row model at the same index, populate blocks if provided
        (settings.rows).forEach((row, rIdx) => {
            const newCell = columnModel ? columnCells[rIdx] : this.createTableCell(newColModel.id);
            row.cells.splice(targetColIndex, 0, newCell);
            newColCells.push(decoupleReference(newCell));
        });
        // Update colgroup visuals
        const colgroup = table.querySelector('colgroup');
        const newColEl = createElement('col');
        colgroup.insertBefore(newColEl, colgroup.children[(settings.enableRowNumbers ? targetColIndex + 1 : targetColIndex)]);
        if (mode === 'percent') {
            // Decide if we can keep equal percent after insertion
            const n = settings.columns.length;
            const canStayPercent = projectEqualPercentFits(containerWidth, n, constants.TABLE_NEW_COL_WIDTH);
            if (canStayPercent) {
                // Equal % distribution
                applyEqualPercent(table, settings);
            }
            else {
                // Switch to px mode: keep existing px widths and give new column 120px
                changeColWidthToPxUnits(table, settings, { index: targetColIndex, width: constants.TABLE_NEW_COL_WIDTH });
            }
        }
        else {
            // Already in px mode: keep existing px widths, assign 120px to new col
            const dataCols = getColgroupChildren(table);
            dataCols[targetColIndex].style.width = `${constants.TABLE_NEW_COL_WIDTH}px`;
        }
        // Update rows DOM
        const rowsEl = Array.from(table.rows);
        rowsEl.forEach((rowEl, rIdx) => {
            const isHeader = rowEl.parentElement.tagName === 'THEAD';
            const newCellEl = createElement(isHeader ? 'th' : 'td');
            newCellEl.dataset.row = isHeader ? '0' : (rIdx).toString();
            newCellEl.dataset.col = targetColIndex.toString();
            newCellEl.tabIndex = 0;
            if (isHeader) {
                newCellEl.textContent = newColModel.headerText;
                newCellEl.setAttribute('role', 'columnheader');
                newCellEl.setAttribute('contenteditable', 'true');
            }
            else {
                newCellEl.setAttribute('role', 'gridcell');
                const cellBlockContainer = createElement('div', {
                    id: (settings.rows[rIdx - (settings.enableHeader ? 1 : 0)].cells[targetColIndex]).id,
                    className: constants.TABLE_CELL_BLK_CONTAINER
                });
                const innerBlock = (settings.rows[rIdx - (settings.enableHeader ? 1 : 0)]
                    .cells[targetColIndex]).blocks[0];
                if (innerBlock) {
                    const innerEl = this.parent.blockRenderer.createBlockElement(innerBlock);
                    cellBlockContainer.appendChild(innerEl);
                }
                newCellEl.appendChild(cellBlockContainer);
            }
            rowEl.insertBefore(newCellEl, rowEl.cells[(settings.enableRowNumbers ? targetColIndex + 1 : targetColIndex)]);
            // Shift col data attributes for cells to the right
            for (let i = targetColIndex + 1; i < rowEl.cells.length; i++) {
                const cell = rowEl.cells[(settings.enableRowNumbers ? i + 1 : i)];
                if (cell && cell.dataset.col) {
                    cell.dataset.col = i.toString();
                }
            }
        });
        this.assertColDataset(table);
        // Focus first cell of the newly added column in the first body row
        const firstBodyRow = table.tBodies[0].rows[0];
        if (firstBodyRow) {
            const domCol = settings.enableRowNumbers ? targetColIndex + 1 : targetColIndex;
            const focusCell = firstBodyRow.cells[domCol];
            if (focusCell) {
                this.removeCellFocus(table);
                this.addCellFocus(focusCell, true);
            }
        }
        const deCoupledColModel = decoupleReference(newColModel);
        this.parent.undoRedoAction.trackTableColumnInsertionForUndoRedo({
            blockId, colIndex: targetColIndex, columnModel: deCoupledColModel, columnCells: newColCells, isUndoRedoAction
        });
        this.triggerBlockUpdate(block, oldBlock);
    }
    /**
     * Deletes a row from the table at the given DOM/visual row index.
     * Updates model, DOM, row numbers, and dataset attributes.
     *
     * @param {ITableRowDeletionOptions} options - arguments needed for row deletion in specified index.
     * @returns {void}
     *
     * @hidden
     */
    deleteRowAt(options) {
        const { blockId, modelIndex, isUndoRedoAction } = options;
        const oldBlock = decoupleReference(getBlockModelById(blockId, this.parent.getEditorBlocks()));
        const blockElement = this.parent.getBlockElementById(blockId);
        const table = blockElement.querySelector('table');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const props = block.properties;
        const tbody = table.tBodies[0];
        if (modelIndex < 0 || modelIndex >= props.rows.length) {
            return;
        }
        const rowModel = props.rows[modelIndex];
        props.rows.splice(modelIndex, 1);
        const rowEl = tbody.rows[modelIndex];
        const previousRowEle = rowEl.previousElementSibling;
        this.removeCellFocus(table);
        if (rowEl) {
            rowEl.remove();
        }
        Array.from(tbody.rows).forEach((row, rIdx) => {
            row.dataset.row = props.enableHeader ? (rIdx + 1).toString() : rIdx.toString();
            Array.from(row.cells).forEach((cell) => {
                if (cell.tagName === 'TD') {
                    const vis = props.enableHeader ? rIdx + 1 : rIdx;
                    cell.dataset.row = vis.toString();
                    cell.setAttribute('aria-rowindex', (rIdx + 1).toString());
                }
            });
        });
        if (tbody.rows.length > 0) {
            this.addCellFocus(tbody.rows[modelIndex - (modelIndex > 0 ? 1 : 0)].cells[0], true);
        }
        else {
            if (props.enableHeader) {
                const headerRow = table.querySelector('thead tr');
                const firstHeaderCell = headerRow.cells[props.enableRowNumbers ? 1 : 0];
                this.addCellFocus(firstHeaderCell, true);
            }
            else {
                const nextBlock = blockElement.nextElementSibling;
                const previousBlock = blockElement.previousElementSibling;
                this.parent.setFocusToBlock(nextBlock || previousBlock);
            }
        }
        this.updateRowNumbers(table, props);
        const deCoupledRowModel = decoupleReference(rowModel);
        this.parent.undoRedoAction.trackTableRowDeletionForUndoRedo({
            blockId, rowIndex: modelIndex, rowModel: deCoupledRowModel, isUndoRedoAction
        });
        this.triggerBlockUpdate(block, oldBlock);
    }
    /**
     * Deletes a column from the table at the given data column index.
     * Updates model, colgroup, DOM cells, and dataset attributes.
     *
     * @param {HTMLTableElement} options - The table element
     * @returns {void}
     *
     * @hidden
     */
    deleteColumnAt(options) {
        const { blockId, colIndex, isUndoRedoAction } = options;
        const oldBlock = decoupleReference(getBlockModelById(blockId, this.parent.getEditorBlocks()));
        const blockElement = this.parent.getBlockElementById(blockId);
        const table = blockElement.querySelector('table');
        const tbody = table.tBodies[0];
        const rowElToUpdateCellFocus = tbody.rows[0];
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const props = block.properties;
        const colModel = props.columns[colIndex];
        const deletedColCells = [];
        const mode = getWidthMode(table);
        // Model
        props.columns.splice(colIndex, 1);
        props.rows.forEach((r) => {
            if (r.cells[colIndex]) {
                deletedColCells.push(decoupleReference(r.cells[colIndex]));
                r.cells.splice(colIndex, 1);
            }
        });
        //DOM
        this.removeCellFocus(table);
        const colgroup = table.querySelector('colgroup');
        const colChildren = Array.from(colgroup.children).filter((col) => !col.classList.contains('e-col-row-number'));
        if (colChildren[colIndex]) {
            colgroup.removeChild(colChildren[colIndex]);
        }
        if (mode === 'percent') {
            // Stay in percent: redistribute equally after deletion
            applyEqualPercent(table, props);
        }
        else {
            changeColWidthToPxUnits(table, props);
        }
        Array.from(table.rows).forEach((row) => {
            const cells = Array.from(row.cells).filter((cell) => !cell.classList.contains('e-row-number'));
            const indexToDelete = Array.from(row.cells).indexOf(cells[colIndex]);
            if (indexToDelete >= 0) {
                row.deleteCell(indexToDelete);
            }
            // Shift col data attributes for cells to the right
            for (let i = colIndex; i < row.cells.length; i++) {
                const cell = row.cells[(props.enableRowNumbers ? i + 1 : i)];
                if (cell && cell.dataset.col) {
                    cell.dataset.col = i.toString();
                }
            }
        });
        this.assertColDataset(table);
        const uiManager = this.parent.blockRenderer.tableRenderer.getManager(blockId);
        if (uiManager) {
            uiManager.hideAllPinnedColBars(); // Removes all .e-col-action-handle.e-pinned
            uiManager.removeRowColSelection(table);
        }
        this.addCellFocus(rowElToUpdateCellFocus.cells[colIndex], true);
        this.updateDataColCount(table, true);
        const deCoupledColModel = decoupleReference(colModel);
        this.parent.undoRedoAction.trackTableColumnDeletionForUndoRedo({
            blockId, colIndex: colIndex, columnModel: deCoupledColModel, columnCells: deletedColCells, isUndoRedoAction
        });
        this.triggerBlockUpdate(block, oldBlock);
    }
    assertColDataset(table) {
        Array.from(table.rows).forEach((row) => {
            const dataCells = Array.from(row.cells).filter((cell) => !cell.classList.contains('e-row-number'));
            dataCells.forEach((cell, dataColIdx) => {
                cell.dataset.col = dataColIdx.toString();
            });
        });
    }
    /**
     * Clears the content of specified table cells (model + DOM).
     *
     * @param {HTMLTableElement} table - The table element
     * @param {HTMLTableCellElement[]} domCells - Collection of table cell elements to clear contents
     * @returns {void}
     *
     * @hidden
     */
    clearCellContents(table, domCells) {
        const blockId = table.getAttribute('data-block-id');
        const oldBlock = decoupleReference(getBlockModelById(blockId, this.parent.getEditorBlocks()));
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const props = block.properties;
        const payloadCells = Array.from(domCells).map((cell) => {
            if (!cell || cell.classList.contains('e-row-number')) {
                return { dataRow: -1, dataCol: -1, prevBlocks: [] };
            }
            const domRow = parseInt(cell.dataset.row, 10);
            const domCol = parseInt(cell.dataset.col, 10);
            const dataRow = props.enableHeader ? domRow - 1 : domRow;
            const dataCol = domCol;
            // Header
            if (domRow === 0 && props.enableHeader && cell.tagName === 'TH') {
                const prevHeaderText = props.columns[dataCol].headerText;
                return { dataRow: -1, dataCol, prevBlocks: [], prevHeaderText, isHeader: true };
            }
            // Body
            const prevBlocks = props.rows[dataRow].cells[dataCol].blocks.map((b) => (Object.assign({}, b)));
            return { dataRow, dataCol, prevBlocks };
        });
        payloadCells.forEach((cell) => this.applyCellChange(table, cell, 'clear'));
        this.parent.undoRedoAction.trackTableCellsClearForUndoRedo({ blockId, cells: payloadCells });
        this.triggerBlockUpdate(block, oldBlock);
    }
    applyCellChange(table, cell, mode) {
        if (cell.isHeader) {
            const headerText = mode === 'restore' ? (cell.prevHeaderText) : '';
            this.setHeaderText(table, cell.dataCol, headerText);
        }
        else {
            const blocks = mode === 'restore' ? (cell.prevBlocks) : [BlockFactory.createParagraphBlock()];
            this.setCellBlocks(table, cell.dataRow, cell.dataCol, blocks);
        }
    }
    /**
     * Replaces the blocks inside a specific table cell with the provided blocks.
     * Updates both the data model and the DOM cell content.
     *
     * @param {HTMLTableElement} table - The table element
     * @param {number} dataRowIndex - The data row index in the model (0-based, excludes header)
     * @param {number} dataColIndex - The data column index in the model (0-based, excludes row-number column)
     * @param {BlockModel[]} blocks - Array of blocks to set as cell content
     * @returns {void}
     *
     * @hidden
     */
    setCellBlocks(table, dataRowIndex, dataColIndex, blocks) {
        const blockId = table.getAttribute('data-block-id');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const props = block.properties;
        if (!props.rows[dataRowIndex] || !props.rows[dataRowIndex].cells[dataColIndex]) {
            return;
        }
        const cell = props.rows[dataRowIndex].cells[dataColIndex];
        cell.blocks = blocks && blocks.length
            ? BlockFactory.populateBlockProperties(blocks, cell.id)
            : [BlockFactory.createParagraphBlock({ parentId: cell.id })];
        const domColIndex = (props.enableRowNumbers ? dataColIndex + 1 : dataColIndex);
        const td = table.tBodies[0].rows[dataRowIndex].cells[domColIndex];
        if (!td) {
            return;
        }
        const container = td.querySelector('.' + constants.TABLE_CELL_BLK_CONTAINER);
        if (container) {
            container.innerHTML = '';
        }
        cell.blocks.forEach((innerBlock) => {
            const innerEl = this.parent.blockRenderer.createBlockElement(innerBlock);
            container.appendChild(innerEl);
        });
    }
    /**
     * Sets the header content with the provided value
     * Updates both the data model and the DOM cell content.
     *
     * @param {HTMLTableElement} table - The table element
     * @param {number} dataColIndex - The data column index in the model (0-based, excludes row-number column)
     * @param {string} text - Value to set
     * @returns {void}
     *
     * @hidden
     */
    setHeaderText(table, dataColIndex, text) {
        const blockId = table.getAttribute('data-block-id');
        const block = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const props = block.properties;
        if (!props.columns[dataColIndex]) {
            return;
        }
        // Model
        props.columns[dataColIndex].headerText = text;
        // DOM
        const th = table.querySelector(`th[data-col="${dataColIndex}"]`);
        th.textContent = text;
    }
    /**
     * Applies visual focus to a table cell and optionally focuses its inner editable block.
     *
     * @param {HTMLElement} cell - The <td> or <th> element to focus
     * @param {boolean} focusInnerBlock - If true, places caret inside the last block of the cell
     * @param {boolean} cursorAtStart - If true and focusing inner block, places cursor at start instead of end
     * @returns {void}
     *
     * @hidden
     */
    addCellFocus(cell, focusInnerBlock, cursorAtStart) {
        if (cell.classList.contains('e-row-number')) {
            // Find the next data cell in the same row
            const row = cell.closest('tr');
            if (row) {
                const next = Array.from(row.cells).find((cell) => !cell.classList.contains('e-row-number'));
                if (next) {
                    return this.addCellFocus(next, true);
                }
            }
            return;
        }
        cell.classList.add(constants.TABLE_CELL_FOCUS);
        if (focusInnerBlock) {
            const tag = cell.tagName.toLowerCase();
            if (tag === 'td') {
                this.shiftFocusToBlockInCell(cell, cursorAtStart);
                return;
            }
            if (tag === 'th') {
                const pos = cursorAtStart ? 0 : (cell.textContent).length;
                this.parent.setFocusToBlock((cell.closest('.' + constants.TABLE_BLOCK_CLS)));
                setCursorPosition(cell, pos);
            }
        }
    }
    /**
     * Removes the focus highlight from all cells in the given table.
     *
     * @param {Element} table - The table element or any element containing the table
     * @returns {void}
     *
     * @hidden
     */
    removeCellFocus(table) {
        removeFocusFromAllCells(table);
    }
    /**
     * Triggers the block update event.
     *
     * @param {BlockModel} block - the block with updated changes.
     * @param {BlockModel} oldBlock - old block
     * @returns {void}
     *
     * @hidden
     */
    triggerBlockUpdate(block, oldBlock) {
        this.parent.eventService.addChange({
            action: 'Update',
            data: {
                block: block,
                prevBlock: oldBlock
            }
        });
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
    shiftFocusToBlockInCell(cell, cursorAtStart) {
        const innerBlockEl = cell.querySelector('.e-block:last-child');
        if (innerBlockEl) {
            const contentEl = getBlockContentElement(innerBlockEl);
            this.parent.setFocusToBlock(innerBlockEl);
            setCursorPosition(contentEl, cursorAtStart ? 0 : contentEl.textContent.length);
        }
    }
    setColumnWidth(colgroup, isAdd) {
        const colChildren = Array.from(colgroup.children).filter((col) => !col.classList.contains('e-col-row-number'));
        const totalCols = colChildren.length + (isAdd ? 1 : 0);
        const newWidth = 100 / totalCols + '%';
        colChildren.forEach((col) => {
            col.style.width = newWidth;
        });
        return newWidth;
    }
    createRowModel(settings) {
        const cells = settings.columns.map((column) => this.createTableCell(column.id));
        return { id: generateUniqueId('row_'), cells };
    }
    createColumnModel(newCount) {
        const newColId = generateUniqueId('col_');
        return { id: newColId, type: 'Text', headerText: `Column ${newCount}` };
    }
    updateDataColCount(table, isDeletion) {
        const currentCount = parseInt(table.getAttribute('data-col-counter'), 10);
        const newCount = currentCount + (isDeletion ? -1 : 1);
        table.setAttribute('data-col-counter', newCount.toString());
    }
    updateRowNumbers(table, settings) {
        if (!settings.enableRowNumbers) {
            return;
        }
        const tbody = table.tBodies[0];
        // Body rows: 1..n (independent of enableHeader)
        Array.from(tbody.rows).forEach((tr, i) => {
            const rnTd = tr.querySelector('td.e-row-number');
            if (rnTd) {
                rnTd.textContent = String(i + 1);
            }
        });
    }
    createTableCell(columnId) {
        const cellId = generateUniqueId('cell_');
        return {
            id: cellId,
            columnId: columnId,
            blocks: [BlockFactory.createParagraphBlock({ parentId: cellId })]
        };
    }
}
